import { faSpinner, faTimes } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import Select, { Props } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { IGetManyResponse } from '../../hooks/useFetchMany'
import { ErrorResponse } from '../../hooks/useRequestHandler'
import { getSelectClasses } from '../../utils/branding-utils'
import { LoadingRow } from '../LoadingRows/LoadingRow'

export interface ISearchableSelectOption<Item> {
  value: Item
  key: string
  label: React.JSX.Element
  isDisabled?: boolean
}

type UsedKeys = 'inputId' | 'name' | 'placeholder' | 'onBlur' | 'isDisabled' | 'isLoading'
interface ISelectProps<Item> extends Pick<Props<Item, false>, UsedKeys> {
  itemName:
    | 'appointment types'
    | 'events'
    | 'contacts'
    | 'products'
    | 'membership packages'
    | 'session packages'
    | 'tags'
    | 'product categories'
    | 'coupons'
    | 'attendees'
    | 'properties'
    | 'property values'
  values: {
    data?: Item
    isLoading: boolean
  }
  searchValues: {
    data?: Item[]
    search?: string
    isLoading: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    setSearch: Dispatch<string>
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined,
    ) => Promise<InfiniteQueryObserverResult<IGetManyResponse<Item>, ErrorResponse>>
  }
  hasError?: boolean
  isClearable?: boolean
  mapToOption: (
    item: Item,
    options: { view: 'option' | 'value'; controlShouldRenderValue?: boolean },
  ) => ISearchableSelectOption<Item>
  onSelect: (val: Item | undefined) => Promise<void> | void
}

export const SearchableSelect = <Item extends { id: string }>(selectProps: ISelectProps<Item>) => {
  const {
    itemName,
    values,
    searchValues,
    hasError = false,
    isClearable = true,
    mapToOption,
    onSelect: handleSelect,
    ...props
  } = selectProps
  const { data: item, isLoading: isLoadingItem } = values
  const {
    data = [],
    search,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    setSearch,
    fetchNextPage,
  } = searchValues
  if (isLoadingItem) return <div className="animate w-100" />
  const defaultValue = item
    ? {
        value: item,
        label: mapToOption(item, { view: 'value' }).label,
      }
    : undefined
  return (
    <Select
      {...props}
      inputValue={search}
      placeholder={props.placeholder || `Search for ${itemName}...`}
      isLoading={isLoading || isFetchingNextPage}
      options={data.map((item) => mapToOption(item, { view: 'option' }))}
      filterOption={() => true}
      formatOptionLabel={(option) => option.label}
      defaultValue={defaultValue}
      onInputChange={async (val: string, meta) => {
        if (meta.action === 'input-change') setSearch(val)
      }}
      onChange={async (val) => {
        if (val) await handleSelect(val.value)
        setSearch('')
      }}
      components={{
        LoadingIndicator: () => (
          <FontAwesomeIcon
            icon={faSpinner}
            className="fa-spin-pulse input-icon position-relative d-inline-block mx-2"
            style={{ width: 20, height: 20 }}
          />
        ),
        DropdownIndicator: () => null,
      }}
      isClearable={isClearable}
      onMenuScrollToBottom={async () => {
        if (isLoading || !hasNextPage) return
        fetchNextPage()
      }}
      classNames={getSelectClasses(hasError)}
      unstyled
      noOptionsMessage={() => `Couldn't find any ${itemName} that match`}
    />
  )
}

interface ISelectCreateProps<Item>
  extends Omit<ISelectProps<Item>, 'values'>,
    Pick<Props<Item, false>, 'autoFocus' | 'onInputChange'> {
  onSelectNew: (val: string) => Promise<void> | void
}

export const SearchableCreateSelect = <Item extends { id: string }>(
  selectProps: ISelectCreateProps<Item>,
) => {
  const {
    itemName,
    searchValues,
    hasError = false,
    mapToOption,
    onSelect: handleSelect,
    onSelectNew: handleSelectNew,
    ...props
  } = selectProps
  const {
    data = [],
    search,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    setSearch,
    fetchNextPage,
  } = searchValues
  return (
    <CreatableSelect
      {...props}
      inputValue={search}
      placeholder="Start typing for suggestions..."
      isLoading={isLoading || isFetchingNextPage}
      options={data.map((item) => mapToOption(item, { view: 'option' }))}
      filterOption={() => true}
      formatOptionLabel={(option) => option.label}
      onInputChange={async (val: string, meta) => {
        if (meta.action === 'input-change') setSearch(val)
        await props.onInputChange?.(val, meta)
      }}
      onChange={async (val: any) => {
        if (!val?.__isNew__ && val?.value) {
          await handleSelect(val.value)
        } else {
          await handleSelectNew((val?.value || '').trim())
        }
        setSearch('')
      }}
      components={{
        LoadingIndicator: () => (
          <FontAwesomeIcon
            icon={faSpinner}
            className="fa-spin-pulse input-icon position-relative d-inline-block mx-2"
            style={{ width: 20, height: 20 }}
          />
        ),
        DropdownIndicator: () => null,
      }}
      createOptionPosition="first"
      isClearable
      onMenuScrollToBottom={async () => {
        if (isLoading || !hasNextPage) return
        fetchNextPage()
      }}
      classNames={getSelectClasses(hasError)}
      unstyled
      noOptionsMessage={() => `Couldn't find any ${itemName} that match`}
    />
  )
}

interface ISelectMultiProps<Item>
  extends Omit<ISelectProps<Item>, 'values' | 'onSelect'>,
    Pick<Props<Item, true>, 'controlShouldRenderValue'> {
  values: {
    data: Item[]
    isLoading: boolean
  }
  initialCount: number
  onSelect: (val: Item[]) => Promise<void> | void
  onRemove?: (val: Item) => Promise<void> | void
}

export const SearchableMultiSelect = <Item extends { id: string }>(
  selectProps: ISelectMultiProps<Item>,
) => {
  const {
    itemName,
    values,
    searchValues,
    initialCount = 0,
    hasError = false,
    mapToOption,
    onSelect: handleSelect,
    controlShouldRenderValue = true,
    ...props
  } = selectProps
  const { data: items = [], isLoading } = values
  const {
    data: searchItems = [],
    isLoading: isSearching,
    setSearch,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = searchValues
  const isLoadingRowVisible = isLoading && initialCount > 0 && items.length === 0
  return (
    <>
      {!controlShouldRenderValue &&
        isLoadingRowVisible &&
        Array.from({ length: initialCount })
          .fill(null)
          .map((_, i) => `loading-${props.inputId || 'option'}-${i}`)
          .map((key) => (
            <LoadingRow
              key={key}
              colProps={{ style: { height: '63px' } }}
              animateProps={{ style: { height: '45px' } }}
            />
          ))}
      {!controlShouldRenderValue &&
        !isLoadingRowVisible &&
        items.map((prod) => (
          <div className="py-2" key={`option:${prod.id}`}>
            <div className="d-flex justify-content-between">
              <span>{mapToOption(prod, { view: 'value', controlShouldRenderValue }).label}</span>
              <button
                type="button"
                onClick={() => {
                  const newProducts = items.filter((p) => p.id !== prod.id)
                  handleSelect(newProducts)
                }}
                className="btn btn-link text-body"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        ))}
      <Select
        {...props}
        placeholder={props.placeholder || `Search for ${itemName}...`}
        isMulti
        controlShouldRenderValue={!!controlShouldRenderValue}
        value={items
          .map((item) => mapToOption(item, { view: 'value', controlShouldRenderValue }))
          .map((i) => ({ ...i, value: { ...i.value, toString: (): string => `value:${i.key}` } }))}
        options={searchItems
          .map((product) => mapToOption(product, { view: 'option', controlShouldRenderValue }))
          .map((i) => ({ ...i, value: { ...i.value, toString: (): string => `option:${i.key}` } }))}
        filterOption={() => true}
        formatOptionLabel={(option: any) => option.label}
        components={{
          LoadingIndicator: () => (
            <FontAwesomeIcon
              icon={faSpinner}
              className="fa-spin-pulse input-icon position-relative d-inline-block mx-2"
              style={{ width: 20, height: 20 }}
            />
          ),
          DropdownIndicator: () => null,
        }}
        isLoading={isLoading || isSearching || isFetchingNextPage}
        classNames={getSelectClasses(hasError)}
        unstyled
        onChange={async (val) => {
          await handleSelect(val.map((v: any) => v.value))
        }}
        onInputChange={(inputText, event) => {
          if (event.action !== 'input-blur' && event.action !== 'menu-close') {
            setSearch(inputText)
          }
        }}
        onMenuScrollToBottom={() => {
          if (hasNextPage) fetchNextPage()
        }}
      />
    </>
  )
}

type ISelectCreateMultiStringProps = Omit<ISelectMultiProps<string>, 'mapToOption' | 'initialCount'>

export const SearchableCreateMultiStringSelect = (selectProps: ISelectCreateMultiStringProps) => {
  const {
    itemName,
    values,
    searchValues,
    hasError = false,
    onSelect: handleSelect,
    ...props
  } = selectProps
  const { data: items = [] } = values
  const {
    data: searchItems = [],
    search,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    setSearch,
    fetchNextPage,
  } = searchValues
  return (
    <CreatableSelect
      {...props}
      isMulti
      inputValue={search}
      placeholder={props.placeholder || `Search for ${itemName}...`}
      isLoading={isLoading || isFetchingNextPage}
      value={items.map((item) => ({ value: item, key: item, label: item }))}
      options={searchItems.map((item) => ({ value: item, key: item, label: item }))}
      filterOption={() => true}
      formatOptionLabel={(option) => option.label}
      onInputChange={async (val: string, meta) => {
        if (meta.action === 'input-change') setSearch(val)
      }}
      onChange={async (val: any) => {
        await handleSelect(val.map((v: any) => v.value))
        setSearch('')
      }}
      components={{
        LoadingIndicator: () => (
          <FontAwesomeIcon
            icon={faSpinner}
            className="fa-spin-pulse input-icon position-relative d-inline-block mx-2"
            style={{ width: 20, height: 20 }}
          />
        ),
        DropdownIndicator: () => null,
      }}
      createOptionPosition="first"
      isClearable
      onMenuScrollToBottom={async () => {
        if (isLoading || !hasNextPage) return
        fetchNextPage()
      }}
      classNames={getSelectClasses(hasError)}
      unstyled
      noOptionsMessage={() => `Couldn't find any ${itemName} that match`}
    />
  )
}
